import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import awani from '@/assets/images/brands/Awani.png';
import catch22 from '@/assets/images/brands/Catch22.png';
import chin from '@/assets/images/brands/ChinChin.png';
import circle from '@/assets/images/brands/Circle.png';
import eatopi from '@/assets/images/brands/Eatopi.png';
import highjoint from '@/assets/images/brands/HighJoint.png';
import operationfalafel from '@/assets/images/brands/OperationFalafel.png';
import taqado from '@/assets/images/brands/Taqado.png';
import zaroob from '@/assets/images/brands/Zaroob.png';
import kitoCoin from '@/assets/images/KitoCoin.svg';
import CashbackEstimation from '@/components/CashbackEstimation/CashbackEstimation';
import { CurvedTextWithStars } from '@/components/CurvedTextWithStars/CurvedTextWithStars';
import { useGetCashbackPercentageValue } from '@/hooks/useGetCashbackPercentageValue';
import { useLanguage } from '@/hooks/useLanguage';
import { useBranding } from '@/utils/branding';
import styles from './SuccessTitle.module.scss';

const brands = [
  {
    name: 'Taqado',
    logo: taqado,
  },
  {
    name: 'Operation Falafel',
    logo: operationfalafel,
  },
  {
    name: 'Awani',
    logo: awani,
  },
  {
    name: 'Circle Cafe',
    logo: circle,
  },
  {
    name: 'Eatopi',
    logo: eatopi,
  },
  {
    name: 'Chin Chin',
    logo: chin,
  },
  {
    name: 'High Joint',
    logo: highjoint,
  },
  {
    name: 'Catch 22',
    logo: catch22,
  },
  {
    name: 'Zaroob',
    logo: zaroob,
  },
];

export const SuccessTitle = () => {
  const { t } = useTranslation();
  const query = new URLSearchParams(useLocation().search);
  const cashbackEstimate = query.get('cashback');
  const signupBonusEligible = query.get('bonus') === '1';
  const cashbackPercentageValue = useGetCashbackPercentageValue();
  const { showAcquisition } = useBranding();
  const { language } = useLanguage();

  if (!showAcquisition) {
    return (
      <div className={styles.root}>
        <div>
          <h1 className={`${styles.title} ${styles.titleNoAcquisitionJourney}`}>
            {t('paymentConfirmation.successTitle.paymentSuccessful')}
          </h1>
          <CurvedTextWithStars
            className={styles.hopeYouEnjoyed}
            shadowColor="transparent"
            starsColor="#c49eed"
            verticalSpacing={65}
            starsPadding={language === 'ar' ? 105 : 10}
            textLines={[
              {
                text: t('paymentConfirmation.paymentConfirmation.hopeYouEnjoyed.firstLine'),
                fontSize: 65,
              },
              {
                text: t('paymentConfirmation.paymentConfirmation.hopeYouEnjoyed.secondLine'),
                fontSize: 65,
              },
              {
                text: t('paymentConfirmation.paymentConfirmation.hopeYouEnjoyed.thirdLine'),
                fontSize: 65,
              },
            ]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div>
        <h1 className={styles.title}>{t('paymentConfirmation.successTitle.paymentSuccessful')}</h1>

        {!!cashbackPercentageValue && (
          <>
            {cashbackEstimate !== '0' ? (
              <img className={styles.kitcon} src={kitoCoin} alt="Kitopi" />
            ) : (
              <CurvedTextWithStars
                className={styles.cashback}
                verticalSpacing={50}
                textLines={[
                  {
                    text: t('paymentConfirmation.successTitle.getUpToXCashback.firstLine', {
                      cashbackPercentageValue,
                    }),
                    fontSize: 50,
                  },
                  {
                    text: t('paymentConfirmation.successTitle.getUpToXCashback.secondLine'),
                    fontSize: 50,
                  },
                ]}
                disableStars
                shadowColor="transparent"
                viewBox="0 40 550 160"
              />
            )}

            {cashbackEstimate !== '0' && (
              <h2 className={styles.subtitle}>
                {t(
                  `paymentConfirmation.successTitle.${signupBonusEligible ? 'signupBonus' : 'youveEarned'}`
                )}
              </h2>
            )}
          </>
        )}

        <CashbackEstimation cashbackEstimate={cashbackEstimate || ''} />

        <p className={styles.description}>{t('paymentConfirmation.successTitle.useAcross')}</p>
      </div>

      <div className={styles.brands}>
        <div>
          {/* First set of brand icons */}
          {brands.map(({ logo, name }) => (
            <img key={`first-${name}`} width={66} src={logo} alt={name} />
          ))}
          {/* Duplicate set for seamless infinite scroll - exact copy of the first set */}
          {brands.map(({ logo, name }) => (
            <img key={`second-${name}`} width={66} src={logo} alt={name} />
          ))}
          {/* Add the first icon again to ensure perfect looping */}
          <img key="extra-first" width={66} src={brands[0].logo} alt={brands[0].name} />
        </div>
      </div>
    </div>
  );
};
